/**
 * Audit Review Panel — Slide-out panel for billing audit workflow.
 * Shows audit reasons, mail-away context, billing breakdown, 
 * and provides approve/adjust/remove/note actions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X,
  AlertTriangle,
  CheckCircle2,
  FileText,
  DollarSign,
  Mail,
  Trash2,
  Pencil,
  ExternalLink,
  MessageSquarePlus,
  RotateCcw,
  Clock,
  User,
  ChevronDown,
  ChevronUp,
  Shield
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { formatDateTimeLocal } from '../../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return formatDateTimeLocal(dateStr);
  } catch { return dateStr; }
};

// Reason label mapping
const REASON_LABELS = {
  billing_review_required: 'Billing review required',
  post_send_cancelled: 'Post-send mail-away cancellation',
  has_additional_charges: 'Additional charges on order'
};

// Section header
const SectionHeader = ({ icon: Icon, title, color = 'text-slate-700' }) => (
  <div className={`flex items-center gap-2 mb-3 ${color}`}>
    <Icon className="w-4 h-4" />
    <h4 className="text-sm font-bold uppercase tracking-wide">{title}</h4>
  </div>
);

// Editable charge row
const ChargeRow = ({ charge, onAdjust, onRemove, isAdjusting }) => {
  const [editing, setEditing] = useState(false);
  const [newAmount, setNewAmount] = useState(charge.amount);
  const [newDescription, setNewDescription] = useState(charge.description || '');
  const label = (charge.charge_type || 'other').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const isLocked = charge.is_locked;

  const handleSave = () => {
    if (newAmount <= 0) { toast.error('Amount must be greater than 0'); return; }
    onAdjust(charge.id, newAmount, newDescription);
    setEditing(false);
  };

  return (
    <div
      data-testid={`charge-row-${charge.id}`}
      className={`flex items-start justify-between p-3 rounded-lg border transition-colors ${
        editing ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200 bg-white hover:bg-slate-50'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
          {isLocked && (
            <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">System</span>
          )}
        </div>
        {editing ? (
          <div className="mt-2 space-y-2">
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Amount</label>
              <input
                type="number"
                step="0.01"
                value={newAmount}
                onChange={(e) => setNewAmount(parseFloat(e.target.value) || 0)}
                className="w-full mt-0.5 px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                data-testid={`charge-amount-input-${charge.id}`}
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Description</label>
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                className="w-full mt-0.5 px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                data-testid={`charge-desc-input-${charge.id}`}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={isAdjusting} className="h-7 text-xs" data-testid={`charge-save-${charge.id}`}>
                {isAdjusting ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setNewAmount(charge.amount); setNewDescription(charge.description || ''); }} className="h-7 text-xs">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500 mt-0.5 truncate">{charge.description || 'No description'}</p>
        )}
        {charge.adjusted_at && !editing && (
          <p className="text-[10px] text-amber-600 mt-1">Adjusted by {charge.adjusted_by_name} on {formatDateTime(charge.adjusted_at)}</p>
        )}
      </div>
      <div className="flex items-center gap-1 ml-3 shrink-0">
        <span className="font-bold text-slate-800 tabular-nums">{formatCurrency(charge.amount)}</span>
        {!isLocked && !editing && (
          <>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50" data-testid={`charge-edit-${charge.id}`}>
              <Pencil className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onRemove(charge.id)} className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`charge-remove-${charge.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const AuditReviewPanel = ({ order, onClose, onOrderUpdated, navigate }) => {
  const [auditNote, setAuditNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [charges, setCharges] = useState(order.additional_charges || []);
  const [auditHistory, setAuditHistory] = useState(order.audit_history || []);
  const [showHistory, setShowHistory] = useState(false);
  const [currentAuditStatus, setCurrentAuditStatus] = useState(order.audit_status || 'pending');

  const basePrice = order.billing_override_price ?? order.billing_unit_price_snapshot ?? 0;
  const additionalTotal = charges.reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalBillable = basePrice + additionalTotal;
  const cancelledMailaway = order.cancelled_mailaway_details || [];
  const isCompleted = currentAuditStatus === 'completed';

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  // Refresh charges from server
  const refreshCharges = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pricing/orders/${order.id}/charges`, getAuthHeader());
      setCharges(data.charges || []);
    } catch {}
  }, [order.id, getAuthHeader]);

  const handleAuditAction = async (action) => {
    if (action === 'add_note' && (!auditNote || !auditNote.trim())) {
      toast.error('Please enter a note before adding.');
      return;
    }
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/pricing/orders/${order.id}/audit/review`,
        { action, notes: auditNote || undefined },
        getAuthHeader()
      );
      toast.success(data.message);
      setCurrentAuditStatus(data.audit_status);
      if (auditNote.trim()) {
        setAuditHistory(prev => [...prev, {
          id: data.note_id || Date.now().toString(),
          action,
          note: auditNote.trim(),
          performed_by_name: 'You',
          performed_at: new Date().toISOString()
        }]);
      }
      setAuditNote('');
      onOrderUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdjustCharge = async (chargeId, newAmount, newDescription) => {
    setIsAdjusting(true);
    try {
      await axios.put(
        `${API}/pricing/orders/${order.id}/charges/${chargeId}`,
        { amount: newAmount, description: newDescription || undefined },
        getAuthHeader()
      );
      toast.success('Charge adjusted');
      await refreshCharges();
      onOrderUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust charge');
    } finally {
      setIsAdjusting(false);
    }
  };

  const handleRemoveCharge = async (chargeId) => {
    if (!window.confirm('Remove this charge? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/pricing/orders/${order.id}/charges/${chargeId}`, getAuthHeader());
      toast.success('Charge removed');
      await refreshCharges();
      onOrderUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove charge');
    }
  };

  return (
    <TooltipProvider>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />

      {/* Panel */}
      <div
        data-testid="audit-review-panel"
        className="fixed top-0 right-0 h-full w-full max-w-[520px] bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-200"
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between shrink-0 ${
          isCompleted ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-3">
            {isCompleted ? (
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
            ) : (
              <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            )}
            <div>
              <h3 className="text-base font-bold text-slate-900">Audit Review</h3>
              <p className="text-xs text-slate-600 font-medium">{order.order_number || order.product_id}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/orders/${order.id}/overview?from=billing`)}
              className="h-8 text-xs font-semibold gap-1"
              data-testid="audit-open-order"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open Order
            </Button>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-black/10 transition-colors" data-testid="audit-panel-close">
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Status Banner */}
          {isCompleted && (
            <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="audit-completed-banner">
              <div>
                <p className="text-sm font-bold text-emerald-800">Audit Completed</p>
                <p className="text-xs text-emerald-600 mt-0.5">
                  by {order.audit_completed_by_name || '—'} on {formatDateTime(order.audit_completed_at)}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAuditAction('reopen')}
                disabled={isSubmitting}
                className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                data-testid="audit-reopen-btn"
              >
                <RotateCcw className="w-3 h-3" />
                Reopen
              </Button>
            </div>
          )}

          {/* Audit Reasons */}
          <div>
            <SectionHeader icon={AlertTriangle} title="Audit Reasons" color="text-red-700" />
            <div className="space-y-2">
              {(order.audit_reasons || []).map((reason, i) => (
                <div key={i} className="flex items-start gap-2.5 p-2.5 bg-red-50 border border-red-100 rounded-lg">
                  <div className="w-5 h-5 rounded-full bg-red-200 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[10px] font-bold text-red-700">{i + 1}</span>
                  </div>
                  <p className="text-sm font-semibold text-red-900">{REASON_LABELS[reason] || reason}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mail-Away Cancellation Context */}
          {cancelledMailaway.length > 0 && (
            <div>
              <SectionHeader icon={Mail} title="Mail-Away Cancellation Details" color="text-amber-700" />
              <div className="space-y-3">
                {cancelledMailaway.map((ma, i) => (
                  <div key={i} className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-2" data-testid={`mailaway-context-${ma.request_id}`}>
                    <p className="text-sm font-bold text-amber-900">{ma.jurisdiction_name}</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                      <div>
                        <span className="text-slate-500">Cancel Type</span>
                        <p className={`font-bold ${ma.cancel_type === 'post_send' ? 'text-red-700' : 'text-blue-700'}`}>
                          {ma.cancel_type === 'post_send' ? 'POST-SEND' : 'PRE-SEND'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Billing Retained</span>
                        <p className={`font-bold ${ma.billing_retained ? 'text-red-700' : 'text-green-700'}`}>
                          {ma.billing_retained ? 'YES' : 'NO'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Review Required</span>
                        <p className={`font-bold ${ma.billing_review_required ? 'text-red-700' : 'text-green-700'}`}>
                          {ma.billing_review_required ? 'YES' : 'NO'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500">Cancelled By</span>
                        <p className="font-medium text-slate-800">{ma.cancelled_by_name || '—'}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-slate-500">Cancelled At</span>
                        <p className="font-medium text-slate-800">{formatDateTime(ma.cancelled_at)}</p>
                      </div>
                      {ma.cancellation_reason && (
                        <div className="col-span-2">
                          <span className="text-slate-500">Reason</span>
                          <p className="font-medium text-slate-800 mt-0.5 p-2 bg-white rounded border border-amber-200">{ma.cancellation_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Billing Breakdown */}
          <div>
            <SectionHeader icon={DollarSign} title="Billing Breakdown" color="text-slate-700" />
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {/* Base Price */}
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                <span className="text-sm text-slate-600">Base Price</span>
                <span className="text-sm font-bold text-slate-800 tabular-nums">{formatCurrency(basePrice)}</span>
              </div>
              {/* Individual Charges */}
              {charges.length > 0 ? (
                <div className="p-3 space-y-2">
                  {charges.map((charge) => (
                    <ChargeRow
                      key={charge.id}
                      charge={charge}
                      onAdjust={handleAdjustCharge}
                      onRemove={handleRemoveCharge}
                      isAdjusting={isAdjusting}
                    />
                  ))}
                </div>
              ) : (
                <div className="px-4 py-3 text-xs text-slate-400 text-center">No additional charges</div>
              )}
              {/* Total */}
              <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
                <span className="text-sm font-bold">Total Billable</span>
                <span className="text-lg font-bold tabular-nums" data-testid="audit-total-billable">{formatCurrency(totalBillable)}</span>
              </div>
            </div>
          </div>

          {/* Audit Notes */}
          <div>
            <SectionHeader icon={MessageSquarePlus} title="Audit Notes" color="text-slate-700" />
            <textarea
              value={auditNote}
              onChange={(e) => setAuditNote(e.target.value)}
              placeholder="Add review notes, observations, or decisions..."
              className="w-full h-20 px-3 py-2 text-sm border border-slate-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 placeholder:text-slate-400"
              data-testid="audit-note-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAuditAction('add_note')}
              disabled={isSubmitting || !auditNote.trim()}
              className="mt-2 h-7 text-xs gap-1"
              data-testid="audit-add-note-btn"
            >
              <MessageSquarePlus className="w-3 h-3" />
              Add Note
            </Button>
          </div>

          {/* Audit History */}
          {auditHistory.length > 0 && (
            <div>
              <button
                className="flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-800 transition-colors mb-2"
                onClick={() => setShowHistory(!showHistory)}
                data-testid="audit-history-toggle"
              >
                <Clock className="w-4 h-4" />
                Audit History ({auditHistory.length})
                {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {showHistory && (
                <div className="space-y-2 ml-1 border-l-2 border-slate-200 pl-3">
                  {auditHistory.slice().reverse().map((h) => (
                    <div key={h.id} className="text-xs">
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <User className="w-3 h-3" />
                        <span className="font-medium">{h.performed_by_name || h.added_by_name}</span>
                        <span>·</span>
                        <span>{formatDateTime(h.performed_at || h.added_at)}</span>
                        {h.action && (
                          <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            h.action === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                            h.action === 'reopen' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{h.action}</span>
                        )}
                      </div>
                      {h.note && <p className="text-slate-700 mt-0.5 leading-relaxed">{h.note}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 shrink-0">
          {!isCompleted ? (
            <div className="flex items-center gap-3">
              <Button
                onClick={() => handleAuditAction('complete')}
                disabled={isSubmitting}
                className="flex-1 h-10 text-sm font-bold gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="audit-approve-btn"
              >
                <Shield className="w-4 h-4" />
                {isSubmitting ? 'Processing...' : 'Approve & Complete Audit'}
              </Button>
              <Button
                variant="outline"
                onClick={onClose}
                className="h-10 text-sm px-5"
              >
                Close
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                Audit completed — approved for invoicing
              </p>
              <Button variant="outline" onClick={onClose} className="h-9 text-sm px-5">
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AuditReviewPanel;
